import { useCallback, useEffect, useState } from 'react';
import { Button, Stack, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { alpha, styled } from '@mui/material/styles';

import { ESignFormTextField } from '@/components/ESign/ESignFormTextField';

import type {
  PublicSignerSignatureValue,
  PublicSignerTypedSignatureValue
} from '../publicSignerTypes';

import { PublicSignerSignatureCanvas } from './PublicSignerSignatureCanvas';

type SignatureMode = 'typed' | 'drawn';

const SignatureModeToggleGroup = styled(ToggleButtonGroup)(({ theme }) => ({
  alignSelf: 'flex-start',
  backgroundColor: alpha(theme.palette.info.main, 0.9),
  borderRadius: 999,
  padding: 4,
  '& .MuiToggleButtonGroup-grouped': {
    border: 0,
    borderRadius: 999,
    color: theme.palette.text.secondary,
    fontSize: 15,
    fontWeight: 700,
    lineHeight: '20px',
    minWidth: 88,
    paddingLeft: 16,
    paddingRight: 16,
    textTransform: 'none'
  },
  '& .MuiToggleButtonGroup-grouped.Mui-selected': {
    backgroundColor: theme.palette.background.paper,
    boxShadow: `0 1px 3px ${alpha(theme.palette.secondary.main, 0.12)}`,
    color: theme.palette.text.primary
  },
  '& .MuiToggleButtonGroup-grouped.Mui-focusVisible': {
    boxShadow: `0 0 0 2px ${theme.palette.primary.main}`
  },
  '& .MuiToggleButtonGroup-grouped:not(:first-of-type)': {
    borderRadius: 999
  },
  '& .MuiToggleButtonGroup-grouped:not(:last-of-type)': {
    borderRadius: 999
  }
}));

function buildTypedSignatureValue(
  typedName: string
): PublicSignerTypedSignatureValue | null {
  if (!typedName.trim()) {
    return null;
  }

  return {
    type: 'typed',
    typed_name: typedName
  };
}

export function PublicSignerSignatureFieldInput({
  drawHelperText = 'Draw your signature in the box.',
  error,
  onChange,
  presetDrawnImageDataUrl,
  presetMode,
  presetTypedName,
  typedHelperText = 'Type your full legal name.',
  typedLabel = 'Typed signature',
  typedMaxLength,
  value
}: {
  drawHelperText?: string;
  error?: string;
  onChange: (value: PublicSignerSignatureValue | null) => void;
  // Presets carry the most-recent typed/drawn signature the user APPLIED
  // on a previous field. They populate the inactive substates so switching
  // modes shows the prior signature; preview-only — the field's draftValue
  // stays null until the user explicitly clicks Apply.
  presetDrawnImageDataUrl: string | null;
  presetMode: SignatureMode | null;
  presetTypedName: string | null;
  typedHelperText?: string;
  typedLabel?: string;
  typedMaxLength?: number;
  value: PublicSignerSignatureValue | null;
}) {
  const [mode, setMode] = useState<SignatureMode>(
    value?.type === 'drawn'
      ? 'drawn'
      : value?.type === 'typed'
        ? 'typed'
        : (presetMode ?? 'typed')
  );
  const [typedName, setTypedName] = useState(
    value?.type === 'typed' ? value.typed_name : (presetTypedName ?? '')
  );
  const [drawnImageDataUrl, setDrawnImageDataUrl] = useState<string | null>(
    value?.type === 'drawn'
      ? value.drawn_image_data_url
      : (presetDrawnImageDataUrl ?? null)
  );

  // Surface the preset to the parent on mount so Apply is enabled. The
  // parent's draftValue is null at this point; without this, the user sees
  // the preset signature but cannot Apply it because the disabled-when-
  // null guard on Apply blocks them.
  // biome-ignore lint/correctness/useExhaustiveDependencies: mount-only
  useEffect(() => {
    if (value !== null) {
      return;
    }
    if (mode === 'typed' && typedName) {
      onChange(buildTypedSignatureValue(typedName));
      return;
    }
    if (mode === 'drawn' && drawnImageDataUrl) {
      onChange({
        type: 'drawn',
        drawn_image_data_url: drawnImageDataUrl
      });
    }
  }, []);

  useEffect(() => {
    if (!value) {
      return;
    }

    if (value.type === 'typed') {
      setMode('typed');
      setTypedName(value.typed_name);
      return;
    }

    setMode('drawn');
    setDrawnImageDataUrl(value.drawn_image_data_url);
  }, [value]);

  const handleModeChange = useCallback(
    (_event: React.MouseEvent<HTMLElement>, nextMode: SignatureMode | null) => {
      if (!nextMode) {
        return;
      }

      setMode(nextMode);

      if (nextMode === 'typed') {
        onChange(buildTypedSignatureValue(typedName));
        return;
      }

      onChange(
        drawnImageDataUrl
          ? {
              type: 'drawn',
              drawn_image_data_url: drawnImageDataUrl
            }
          : null
      );
    },
    [drawnImageDataUrl, onChange, typedName]
  );

  const handleTypedNameChange = useCallback(
    (nextTypedName: string) => {
      setTypedName(nextTypedName);
      onChange(buildTypedSignatureValue(nextTypedName));
    },
    [onChange]
  );

  const handleDrawnSignatureChange = useCallback(
    (nextDrawnImageDataUrl: string | null) => {
      setDrawnImageDataUrl(nextDrawnImageDataUrl);
      onChange(
        nextDrawnImageDataUrl
          ? {
              type: 'drawn',
              drawn_image_data_url: nextDrawnImageDataUrl
            }
          : null
      );
    },
    [onChange]
  );

  const handleClear = useCallback(() => {
    if (mode === 'typed') {
      setTypedName('');
      onChange(null);
      return;
    }

    setDrawnImageDataUrl(null);
    onChange(null);
  }, [mode, onChange]);

  const hasValue =
    mode === 'typed' ? Boolean(typedName.trim()) : Boolean(drawnImageDataUrl);

  return (
    <Stack spacing={1.5}>
      <Stack
        alignItems="center"
        direction="row"
        justifyContent="space-between"
        spacing={1.5}
      >
        <SignatureModeToggleGroup
          color="primary"
          exclusive
          onChange={handleModeChange}
          size="small"
          value={mode}
        >
          <ToggleButton value="typed">Type</ToggleButton>
          <ToggleButton value="drawn">Draw</ToggleButton>
        </SignatureModeToggleGroup>
        {hasValue ? (
          <Button
            onClick={handleClear}
            size="small"
            sx={{ flexShrink: 0, textTransform: 'none' }}
            variant="text"
          >
            Clear
          </Button>
        ) : null}
      </Stack>
      {mode === 'typed' ? (
        <ESignFormTextField
          error={Boolean(error)}
          fullWidth
          helperText={error ?? typedHelperText}
          inputProps={
            typedMaxLength ? { maxLength: typedMaxLength } : undefined
          }
          label={typedLabel}
          onChange={(event) => {
            handleTypedNameChange(event.target.value);
          }}
          value={typedName}
        />
      ) : (
        <PublicSignerSignatureCanvas
          error={error}
          helperText={drawHelperText}
          onChange={handleDrawnSignatureChange}
          value={drawnImageDataUrl}
        />
      )}
    </Stack>
  );
}
